import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const dashboardPath =
    user?.role === "admin" ? "/admin" : user?.role === "employer" ? "/employer" : "/applicant";

  const NavLinks = ({ onClick }) => (
    <>
      <Link to="/jobs" onClick={onClick} data-testid="nav-browse-jobs" className="text-sm font-semibold text-slate-700 hover:text-amber-600 transition-colors">Browse Jobs</Link>
      <Link to="/post-job" onClick={onClick} data-testid="nav-post-job" className="text-sm font-semibold text-slate-700 hover:text-amber-600 transition-colors">Post a Job <span className="text-amber-600">$99</span></Link>
      <Link to="/membership" onClick={onClick} data-testid="nav-membership" className="text-sm font-semibold text-slate-700 hover:text-amber-600 transition-colors">Membership <span className="text-amber-600">$10</span></Link>
    </>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/90 border-b border-slate-200">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex items-center justify-between h-16">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group">
          <img src="/safety-jobs-logo.png" alt="Safety Jobs" className="h-10 w-10 rounded-lg shadow-sm group-hover:scale-105 transition-transform" />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-navy-800 text-lg">Safety Jobs</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold -mt-0.5">Job Board</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLinks />
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="user-menu-trigger" className="gap-2 border-slate-300">
                  <LayoutDashboard className="h-4 w-4" /> {user.name?.split(" ")[0] || "Account"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-xs text-slate-500">{user.email}</div>
                <div className="px-2 pb-1.5 text-[11px] uppercase tracking-wide font-semibold text-amber-600">{user.role}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate(dashboardPath)}>
                  <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" data-testid="nav-signin" onClick={() => navigate("/login")}>Sign in</Button>
              <Button data-testid="nav-signup" onClick={() => navigate("/register")} className="bg-navy-800 hover:bg-navy-900 text-white">Get started</Button>
            </>
          )}
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-4 flex flex-col gap-4">
          <NavLinks onClick={() => setOpen(false)} />
          <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
            {user ? (
              <>
                <Button variant="outline" onClick={() => { navigate(dashboardPath); setOpen(false); }}>Dashboard</Button>
                <Button variant="ghost" onClick={async () => { await logout(); navigate("/"); setOpen(false); }}>Sign out</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => { navigate("/login"); setOpen(false); }}>Sign in</Button>
                <Button className="bg-navy-800 text-white" onClick={() => { navigate("/register"); setOpen(false); }}>Get started</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
